'use client'

import * as React from 'react'
import { ChevronLeft, ChevronRight, CheckCircle2, XCircle, Plane, Home, PartyPopper, Moon, CircleDashed, AlertTriangle } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { DayStatus } from '@/types/monthly'
import { cn, formatTime } from '@/lib/utils'
import { todayIST, isWeekendYMD } from '@/lib/date-utils'

export interface CalendarDay {
  date: string
  status: DayStatus
  hours?: number
  checkIn?: string | null
  checkOut?: string | null
  workMode?: string | null
  incomplete?: boolean
}

/**
 * Cells for days after today are rendered as FUTURE rather than ABSENT — an
 * unworked day that hasn't happened yet is not an absence.
 */
type CellStatus = DayStatus | 'FUTURE'

interface StatusMeta {
  icon: LucideIcon
  label: string
  cell: string
  icon_: string
  dot: string
}

const STATUS: Record<CellStatus, StatusMeta> = {
  PRESENT: {
    icon: CheckCircle2,
    label: 'Present',
    cell: 'bg-emerald-50 ring-emerald-200 hover:ring-emerald-300 dark:bg-emerald-500/10 dark:ring-emerald-500/25 dark:hover:ring-emerald-500/40',
    icon_: 'text-emerald-600 dark:text-emerald-300',
    dot: 'bg-emerald-500',
  },
  ABSENT: {
    icon: XCircle,
    label: 'Absent',
    cell: 'bg-rose-50 ring-rose-200 hover:ring-rose-300 dark:bg-rose-500/10 dark:ring-rose-500/25 dark:hover:ring-rose-500/40',
    icon_: 'text-rose-600 dark:text-rose-300',
    dot: 'bg-rose-500',
  },
  LEAVE: {
    icon: Plane,
    label: 'Leave',
    cell: 'bg-amber-50 ring-amber-200 hover:ring-amber-300 dark:bg-amber-500/10 dark:ring-amber-500/25 dark:hover:ring-amber-500/40',
    icon_: 'text-amber-600 dark:text-amber-300',
    dot: 'bg-amber-500',
  },
  WFH_NO_LOG: {
    icon: Home,
    label: 'WFH',
    cell: 'bg-violet-50 ring-violet-200 hover:ring-violet-300 dark:bg-violet-500/10 dark:ring-violet-500/25 dark:hover:ring-violet-500/40',
    icon_: 'text-violet-600 dark:text-violet-300',
    dot: 'bg-violet-500',
  },
  HOLIDAY: {
    icon: PartyPopper,
    label: 'Holiday',
    cell: 'bg-cyan-50 ring-cyan-200 hover:ring-cyan-300 dark:bg-cyan-500/10 dark:ring-cyan-500/25 dark:hover:ring-cyan-500/40',
    icon_: 'text-cyan-600 dark:text-cyan-300',
    dot: 'bg-cyan-500',
  },
  WEEKEND: {
    icon: Moon,
    label: 'Weekend',
    cell: 'bg-neutral-50 ring-neutral-200/70 dark:bg-white/[0.02] dark:ring-white/5',
    icon_: 'text-neutral-300 dark:text-white/20',
    dot: 'bg-neutral-300 dark:bg-white/20',
  },
  FUTURE: {
    icon: CircleDashed,
    label: '',
    cell: 'bg-transparent ring-neutral-200/60 dark:ring-white/5',
    icon_: 'text-neutral-200 dark:text-white/10',
    dot: 'bg-neutral-200 dark:bg-white/10',
  },
}

const LEGEND: CellStatus[] = ['PRESENT', 'ABSENT', 'LEAVE', 'WFH_NO_LOG', 'HOLIDAY', 'WEEKEND']

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

function monthLabel(month: string) {
  const [y, m] = month.split('-').map(Number)
  return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
}

function shiftMonth(month: string, delta: number) {
  const [y, m] = month.split('-').map(Number)
  const d = new Date(y, m - 1 + delta, 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function fmtHours(h?: number) {
  if (!h || h <= 0) return null
  return `${h % 1 === 0 ? h : h.toFixed(1)}h`
}

interface AttendanceCalendarProps {
  month: string
  onMonthChange?: (month: string) => void
  /** Keyed by YYYY-MM-DD. Days absent from the map fall back to WEEKEND/ABSENT/FUTURE. */
  days: Record<string, CalendarDay>
  onDayClick?: (day: CalendarDay) => void
  /** Shown under the header, e.g. "Click a day to edit". */
  hint?: string
  className?: string
}

export function AttendanceCalendar({
  month,
  onMonthChange,
  days,
  onDayClick,
  hint,
  className,
}: AttendanceCalendarProps) {
  const today = todayIST()
  const [year, mon] = month.split('-').map(Number)

  const cells = React.useMemo(() => {
    const first = new Date(year, mon - 1, 1)
    // Grid is Monday-first; getDay() is Sunday-first, so rotate by 6.
    const lead = (first.getDay() + 6) % 7
    const daysInMonth = new Date(year, mon, 0).getDate()
    const out: (CalendarDay & { cellStatus: CellStatus; day: number } | null)[] = []
    for (let i = 0; i < lead; i++) out.push(null)
    for (let d = 1; d <= daysInMonth; d++) {
      const date = `${month}-${String(d).padStart(2, '0')}`
      const entry = days[date]
      const isWeekend = isWeekendYMD(date)
      let cellStatus: CellStatus
      if (entry) cellStatus = entry.status
      else if (isWeekend) cellStatus = 'WEEKEND'
      else if (date > today) cellStatus = 'FUTURE'
      else cellStatus = 'ABSENT'
      out.push({ ...entry, date, day: d, status: cellStatus === 'FUTURE' ? 'ABSENT' : cellStatus, cellStatus })
    }
    while (out.length % 7 !== 0) out.push(null)
    return out
  }, [month, year, mon, days, today])

  const summary = React.useMemo(() => {
    const counts: Partial<Record<CellStatus, number>> = {}
    for (const c of cells) {
      if (!c || c.cellStatus === 'FUTURE') continue
      counts[c.cellStatus] = (counts[c.cellStatus] || 0) + 1
    }
    return counts
  }, [cells])

  return (
    <div className={cn('space-y-3', className)}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-1">
          {onMonthChange && (
            <button
              type="button"
              aria-label="Previous month"
              onClick={() => onMonthChange(shiftMonth(month, -1))}
              className="rounded-lg p-1.5 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900 dark:text-white/50 dark:hover:bg-white/10 dark:hover:text-white transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          )}
          <h3 className="min-w-[9.5rem] text-center text-sm font-semibold tracking-tight text-neutral-900 dark:text-white/90 sm:text-left">
            {monthLabel(month)}
          </h3>
          {onMonthChange && (
            <button
              type="button"
              aria-label="Next month"
              onClick={() => onMonthChange(shiftMonth(month, 1))}
              className="rounded-lg p-1.5 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900 dark:text-white/50 dark:hover:bg-white/10 dark:hover:text-white transition-colors"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-neutral-500 dark:text-white/50">
          {LEGEND.map((s) => {
            const meta = STATUS[s]
            const n = summary[s]
            if (!n) return null
            return (
              <span key={s} className="inline-flex items-center gap-1.5">
                <span className={cn('h-1.5 w-1.5 rounded-full', meta.dot)} />
                {meta.label}
                <span className="tabular-nums font-medium text-neutral-700 dark:text-white/70">{n}</span>
              </span>
            )
          })}
        </div>
      </div>

      {hint && <p className="text-[11px] text-neutral-400 dark:text-white/40">{hint}</p>}

      <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
        {WEEKDAYS.map((w) => (
          <div
            key={w}
            className="pb-1 text-center text-[10px] font-medium uppercase tracking-wider text-neutral-400 dark:text-white/40"
          >
            <span className="hidden sm:inline">{w}</span>
            <span className="sm:hidden">{w[0]}</span>
          </div>
        ))}

        {cells.map((c, i) => {
          if (!c) return <div key={`pad-${i}`} />
          const meta = STATUS[c.cellStatus]
          const Icon = meta.icon
          const isToday = c.date === today
          const hours = fmtHours(c.hours)
          const clickable = !!onDayClick && c.cellStatus !== 'FUTURE'
          const sub =
            c.cellStatus === 'PRESENT'
              ? hours || (c.incomplete ? 'open' : '')
              : c.cellStatus === 'FUTURE' || c.cellStatus === 'WEEKEND'
                ? ''
                : meta.label
          const title = [
            c.date,
            meta.label || undefined,
            c.checkIn ? `In ${formatTime(c.checkIn)}` : undefined,
            c.checkOut ? `Out ${formatTime(c.checkOut)}` : undefined,
            c.workMode || undefined,
          ]
            .filter(Boolean)
            .join(' · ')

          return (
            <button
              key={c.date}
              type="button"
              title={title}
              disabled={!clickable}
              onClick={clickable ? () => onDayClick!(c) : undefined}
              className={cn(
                'relative flex aspect-square flex-col items-center justify-center gap-0.5 rounded-xl p-1 ring-1 ring-inset transition-all',
                meta.cell,
                clickable && 'cursor-pointer hover:-translate-y-0.5 motion-reduce:hover:translate-y-0',
                !clickable && 'cursor-default',
                isToday && 'ring-2 ring-orange-500 dark:ring-orange-400'
              )}
            >
              <span
                className={cn(
                  'absolute left-1.5 top-1 text-[10px] font-medium tabular-nums',
                  isToday
                    ? 'text-orange-600 dark:text-orange-300'
                    : 'text-neutral-400 dark:text-white/40'
                )}
              >
                {c.day}
              </span>
              {c.incomplete && c.cellStatus === 'PRESENT' && (
                <AlertTriangle
                  className="absolute right-1 top-1 h-2.5 w-2.5 text-amber-500 dark:text-amber-400"
                  aria-label="Incomplete"
                />
              )}
              <Icon className={cn('h-4 w-4 sm:h-[18px] sm:w-[18px]', meta.icon_)} />
              {sub && (
                <span
                  className={cn(
                    'max-w-full truncate text-[9px] leading-none tabular-nums sm:text-[10px]',
                    meta.icon_
                  )}
                >
                  {sub}
                </span>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}
